import { saveAccountCredentials, type CredentialTranslator } from './credentialSave'
import type {
  AccountResponse,
  EmailEligibilityResponse,
  OAuthHandoff,
  ProUpsellResponse,
  PurchaseResponse,
  PurchaseStatusResponse,
  SignupConfiguration,
  SignupFormState,
  SignupStep,
  WelcomeEmailResponse,
} from './types'

const supportedSteps: SignupStep[] = [
  'bundle',
  'purchase',
  'account',
  'addons-pro',
  'done',
]

const supportedLanguages = [
  'en', 'es', 'fr', 'de', 'pt', 'it', 'ru', 'tr', 'ko', 'ja',
  'da', 'hu', 'no', 'pl', 'sv', 'zh', 'zh_TW', 'hi', 'tl', 'th',
]

export function getSignupId(): string {
  const signupPart = window.location.pathname.split('/').filter(Boolean)[0]
  return signupPart ? decodeURIComponent(signupPart) : 'idecide'
}

export function getStepFromPath(): SignupStep {
  const candidate = window.location.pathname.split('/').filter(Boolean)[1] as
    | SignupStep
    | undefined
  return candidate && supportedSteps.includes(candidate) ? candidate : 'bundle'
}

export function getStepPath(signupId: string, step: SignupStep): string {
  return `/${encodeURIComponent(signupId)}/${step}${window.location.search}`
}

export function setStepPath(
  signupId: string,
  step: SignupStep,
  purchaseFlowId: string | null = null,
): void {
  window.history.pushState(
    { step, purchaseFlowId },
    '',
    getStepPath(signupId, step),
  )
}

export function setLanguageQuery(languageCode: string): void {
  const url = new URL(window.location.href)
  url.searchParams.set('lang', languageCode)
  window.history.replaceState(window.history.state, '', url)
}

/**
 * Drops the OAuth hand-off id from the URL, so the steps that follow (and a reload) no longer carry it.
 */
export function clearOAuthHandoffQuery(): void {
  const url = new URL(window.location.href)
  url.searchParams.delete('oauth_signup')
  window.history.replaceState(window.history.state, '', url)
}

export function getLanguageQuery(): string {
  const requested = new URLSearchParams(window.location.search).get('lang')

  if (requested) {
    const normalized = requested.replace('-', '_')
    return supportedLanguages.find(
      (language) => language.toLowerCase() === normalized.toLowerCase(),
    ) ?? 'en'
  }

  const browserLanguage = (navigator.language || 'en').replace('-', '_')
  const exact = supportedLanguages.find(
    (language) => language.toLowerCase() === browserLanguage.toLowerCase(),
  )

  if (exact)
    return exact

  const prefix = browserLanguage.slice(0, 2).toLowerCase()
  return supportedLanguages.find(
    (language) => language.toLowerCase() === prefix,
  ) ?? 'en'
}

export async function loadConfiguration(
  signupId: string,
  languageCode: string,
  signal?: AbortSignal,
): Promise<SignupConfiguration> {
  const response = await fetch(
    `/modern/${encodeURIComponent(signupId)}/configuration?lang=${encodeURIComponent(languageCode)}`,
    {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal,
    },
  )

  if (response.status === 404) {
    throw new Error('This signup link is not available.')
  }

  if (!response.ok) {
    throw new Error('We could not load this signup. Please try again.')
  }

  return response.json() as Promise<SignupConfiguration>
}

/**
 * The OAuth application the visitor came from, when the backoffice's OAuth login page sent them here with a
 * hand-off id. An unknown or expired id just means the signup finishes the ordinary way.
 */
export async function loadOAuthHandoff(
  signupId: string,
  signal?: AbortSignal,
): Promise<OAuthHandoff | null> {
  const handoffId = new URLSearchParams(window.location.search).get('oauth_signup')

  if (!handoffId)
    return null

  const response = await fetch(
    `/modern/${encodeURIComponent(signupId)}/oauth-handoff?id=${encodeURIComponent(handoffId)}`,
    {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal,
    },
  )

  if (!response.ok)
    return null

  return response.json() as Promise<OAuthHandoff>
}

export async function checkEmailEligibility(
  signupId: string,
  languageCode: string,
  emailAddress: string,
): Promise<EmailEligibilityResponse> {
  const response = await fetch(
    `/modern/${encodeURIComponent(signupId)}/email-eligibility`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
      body: JSON.stringify({ emailAddress, languageCode }),
    },
  )
  const responseBody = await response.json().catch(() => null) as
    | EmailEligibilityResponse
    | null

  if (responseBody && typeof responseBody.status === 'string')
    return responseBody

  throw new Error('We could not check this email address. Please try again.')
}

export async function loadPurchaseStatus(
  signupId: string,
  signal?: AbortSignal,
): Promise<PurchaseStatusResponse> {
  const response = await fetch(
    `/modern/${encodeURIComponent(signupId)}/purchase/status`,
    {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal,
    },
  )

  if (!response.ok)
    throw new Error('We could not confirm the current purchase status.')

  return response.json() as Promise<PurchaseStatusResponse>
}

export async function clearPurchaseStatus(signupId: string): Promise<void> {
  const response = await fetch(
    `/modern/${encodeURIComponent(signupId)}/purchase/status`,
    {
      method: 'POST',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    },
  )

  if (!response.ok)
    throw new Error('We could not restart this signup.')
}

export async function updatePurchaseLanguage(
  signupId: string,
  languageCode: string,
): Promise<void> {
  const response = await fetch(
    `/modern/${encodeURIComponent(signupId)}/purchase/status/language?lang=${encodeURIComponent(languageCode)}`,
    {
      method: 'PUT',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    },
  )

  if (!response.ok)
    throw new Error('We could not save the selected language.')
}

export async function submitPurchase(
  signupId: string,
  languageCode: string,
  purchaseAttemptId: string,
  form: SignupFormState,
  signal?: AbortSignal,
): Promise<PurchaseResponse> {
  const [expiryMonth, expiryYear] = form.cardExpiry
    .split('/')
    .map((value) => Number(value))
  const urlParameters = new URLSearchParams(window.location.search)

  const response = await fetch(
    `/modern/${encodeURIComponent(signupId)}/purchase`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
      signal,
      body: JSON.stringify({
        purchaseAttemptId,
        packageId: form.packageId,
        languageCode,
        emailAddress: form.emailAddress,
        password: form.password,
        confirmPassword: form.confirmPassword,
        firstName: form.firstName,
        lastName: form.lastName,
        phoneNumber: form.phoneNumber,
        cardName: form.cardName,
        cardNumber: form.cardNumber,
        cardCode: form.cardCode,
        cardExpiryMonth: expiryMonth,
        cardExpiryYear: expiryYear < 100 ? 2000 + expiryYear : expiryYear,
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        city: form.city,
        countryId: form.countryId,
        stateProvinceId: form.stateProvinceId,
        postalZip: form.postalZip,
        termsAccepted: form.termsAccepted,
        fbclid: urlParameters.get('fbclid') ?? '',
      }),
    },
  )

  const responseBody = await response.json().catch(() => null) as
    | PurchaseResponse
    | null

  if (responseBody && typeof responseBody.status === 'string'
      && typeof responseBody.message === 'string')
    return responseBody

  throw new Error(
    response.ok
      ? 'The server returned an invalid purchase response.'
      : 'We could not confirm the purchase result. Do not retry yet; please contact support.',
  )
}

async function readAccountResponse(
  response: Response,
  fallbackMessage: string,
): Promise<AccountResponse> {
  const responseBody = await response.json().catch(() => null) as
    | AccountResponse
    | null

  if (responseBody && typeof responseBody.status === 'string'
      && typeof responseBody.message === 'string')
    return responseBody

  throw new Error(fallbackMessage)
}

export async function updateAccountCredentials(
  signupId: string,
  flowId: string,
  emailAddress: string,
  password: string,
  confirmPassword: string,
  t: CredentialTranslator,
): Promise<AccountResponse> {
  return saveAccountCredentials(signupId, flowId, emailAddress, password, confirmPassword, t)
}

export async function finalizeAccount(
  signupId: string,
  flowId: string,
  languageCode: string,
  form: SignupFormState,
): Promise<AccountResponse> {
  const response = await fetch(
    `/modern/${encodeURIComponent(signupId)}/account`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
      body: JSON.stringify({
        flowId,
        languageCode,
        accountFields: form.accountFields,
        marketingSourceId: form.marketingSourceId,
      }),
    },
  )

  return readAccountResponse(
    response,
    'We could not finish account setup. Your payment will not be submitted again.',
  )
}

export async function resendWelcomeEmail(
  signupId: string,
  flowId: string,
): Promise<WelcomeEmailResponse> {
  const response = await fetch(
    `/modern/${encodeURIComponent(signupId)}/account/welcome-email?flowId=${encodeURIComponent(flowId)}`,
    {
      method: 'POST',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    },
  )
  const responseBody = await response.json().catch(() => null) as
    | WelcomeEmailResponse
    | null

  if (responseBody && typeof responseBody.status === 'string'
      && typeof responseBody.message === 'string')
    return responseBody

  throw new Error('We could not resend the welcome email. Please try again.')
}

export async function submitProUpsell(
  signupId: string,
  flowId: string,
  upgradeAttemptId: string,
): Promise<ProUpsellResponse> {
  const response = await fetch(
    `/modern/${encodeURIComponent(signupId)}/upsell/pro`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
      body: JSON.stringify({ flowId, upgradeAttemptId }),
    },
  )
  const responseBody = await response.json().catch(() => null) as
    | ProUpsellResponse
    | null

  if (responseBody && typeof responseBody.status === 'string')
    return responseBody

  throw new Error('We could not confirm the Pro upgrade. Please do not retry yet.')
}
